import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listTakeoutMenus,
  createTakeoutMenu,
  updateTakeoutMenu,
  deleteTakeoutMenu,
  seedDefaultTakeoutMenus,
  type TakeoutMenu,
} from '../../api/takeoutMenu.api';
import { useAuth } from '../../contexts/AuthContext';
import { DEMO_MENUS } from '../../utils/demoData';
import styles from './TakeoutMenuManager.module.css';

interface Props {
  storeId: string;
}

interface FormState {
  name: string;
  description: string;
  category: string;
  price: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  category: '',
  price: '',
};

export default function TakeoutMenuManager({ storeId }: Props) {
  const { isDemo } = useAuth();
  const [menus, setMenus] = useState<TakeoutMenu[]>(isDemo ? DEMO_MENUS : []);
  const [isLoading, setIsLoading] = useState(!isDemo);
  const [error, setError] = useState<string | null>(null);

  const [newForm, setNewForm] = useState<FormState>(EMPTY_FORM);
  const [isAdding, setIsAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);

  const load = useCallback(async () => {
    if (!storeId) return;
    if (isDemo) {
      setMenus(DEMO_MENUS);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await listTakeoutMenus(storeId, true);
      setMenus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : '메뉴 조회 실패');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, isDemo]);

  useEffect(() => {
    load();
  }, [load]);

  const parsePrice = (raw: string): number | null => {
    const clean = raw.replace(/[^0-9]/g, '');
    if (!clean) return null;
    const n = parseInt(clean, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  const validateForm = (form: FormState): string | null => {
    if (!form.name.trim()) return '메뉴 이름을 입력해주세요.';
    if (parsePrice(form.price) === null) return '가격을 숫자로 입력해주세요.';
    return null;
  };

  const handleAdd = async () => {
    if (isAdding) return;
    if (isDemo) {
      setError('데모 모드에서는 메뉴 추가가 저장되지 않습니다.');
      return;
    }
    const msg = validateForm(newForm);
    if (msg) {
      setError(msg);
      return;
    }
    setError(null);
    setIsAdding(true);
    try {
      await createTakeoutMenu(storeId, {
        name: newForm.name.trim(),
        description: newForm.description.trim() || null,
        category: newForm.category.trim() || null,
        price: parsePrice(newForm.price)!,
        displayOrder: menus.length,
      });
      setNewForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '메뉴 추가 실패');
    } finally {
      setIsAdding(false);
    }
  };

  const startEdit = (menu: TakeoutMenu) => {
    setEditingId(menu.id);
    setEditForm({
      name: menu.name,
      description: menu.description ?? '',
      category: menu.category ?? '',
      price: String(menu.price),
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const saveEdit = async () => {
    if (!editingId || isSaving) return;
    if (isDemo) {
      setError('데모 모드에서는 변경이 저장되지 않습니다.');
      return;
    }
    const msg = validateForm(editForm);
    if (msg) {
      setError(msg);
      return;
    }
    setIsSaving(true);
    try {
      await updateTakeoutMenu(storeId, editingId, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || null,
        category: editForm.category.trim() || null,
        price: parsePrice(editForm.price)!,
      });
      cancelEdit();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '저장 실패');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (menu: TakeoutMenu) => {
    if (isDemo) {
      // 데모 모드는 로컬 상태만 토글 (서버 호출 안 함)
      setMenus((prev) => prev.map((m) => (m.id === menu.id ? { ...m, isActive: !m.isActive } : m)));
      return;
    }
    try {
      await updateTakeoutMenu(storeId, menu.id, { isActive: !menu.isActive });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '상태 변경 실패');
    }
  };

  const handleDelete = async (menu: TakeoutMenu) => {
    if (!window.confirm(`"${menu.name}" 메뉴를 삭제하시겠습니까?`)) return;
    if (isDemo) {
      setError('데모 모드에서는 메뉴 삭제가 저장되지 않습니다.');
      return;
    }
    try {
      await deleteTakeoutMenu(storeId, menu.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '삭제 실패');
    }
  };

  const handleSeedDefault = async () => {
    if (isSeeding) return;
    if (isDemo) {
      setError('데모 모드에서는 기본 메뉴 불러오기가 저장되지 않습니다.');
      return;
    }
    if (!window.confirm('기본 메뉴 3개를 불러오시겠습니까? (현재 메뉴가 비어있을 때만 가능)')) return;
    setIsSeeding(true);
    setError(null);
    try {
      await seedDefaultTakeoutMenus(storeId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '기본 메뉴 불러오기 실패');
    } finally {
      setIsSeeding(false);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, TakeoutMenu[]>();
    for (const m of menus) {
      const key = m.category?.trim() || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m);
    }
    return map;
  }, [menus]);

  return (
    <div className={styles.container}>
      <p className={styles.hint}>
        포장 주문 시 키오스크에서 고객이 선택할 수 있는 메뉴를 등록하세요. 설명과 카테고리는 선택 입력입니다.
      </p>

      {isLoading && <div className={styles.loading}>로딩 중...</div>}

      {!isLoading && (
        <>
          {menus.length === 0 ? (
            <div className={styles.empty}>
              등록된 메뉴가 없습니다.
              <button
                type="button"
                className={styles.btnSeed}
                onClick={handleSeedDefault}
                disabled={isSeeding}
              >
                {isSeeding ? '불러오는 중...' : '기본 메뉴 3개 불러오기'}
              </button>
            </div>
          ) : (
            <ul className={styles.list}>
              {Array.from(grouped.entries()).map(([cat, items]) => (
                <React.Fragment key={cat || '__uncategorized__'}>
                  {cat && <li className={styles.categoryHeader}>{cat}</li>}
                  {items.map((menu) => (
                    <li key={menu.id} className={styles.item}>
                      {editingId === menu.id ? (
                        <div className={styles.editForm}>
                          <div className={styles.editRow}>
                            <input
                              className={styles.input}
                              value={editForm.name}
                              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              placeholder="메뉴 이름"
                              disabled={isSaving}
                            />
                            <input
                              className={styles.priceInput}
                              value={editForm.price}
                              onChange={(e) =>
                                setEditForm({ ...editForm, price: e.target.value.replace(/[^0-9]/g, '') })
                              }
                              placeholder="가격"
                              inputMode="numeric"
                              disabled={isSaving}
                            />
                          </div>
                          <div className={styles.editRow}>
                            <input
                              className={styles.input}
                              value={editForm.category}
                              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                              placeholder="카테고리 (예: 커리, 난밥) — 선택"
                              disabled={isSaving}
                            />
                          </div>
                          <div className={styles.editRow}>
                            <textarea
                              className={styles.textarea}
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                              placeholder="메뉴 설명 (선택)"
                              rows={2}
                              disabled={isSaving}
                            />
                          </div>
                          <div className={styles.editActions}>
                            <button type="button" className={styles.btnSave} onClick={saveEdit} disabled={isSaving}>
                              {isSaving ? '...' : '저장'}
                            </button>
                            <button type="button" className={styles.btnCancel} onClick={cancelEdit} disabled={isSaving}>
                              취소
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={styles.row}>
                          <div className={`${styles.menuInfo} ${!menu.isActive ? styles.inactive : ''}`}>
                            <div className={styles.menuTopRow}>
                              <span className={styles.menuName}>{menu.name}</span>
                              <span className={styles.menuPrice}>{menu.price.toLocaleString()}원</span>
                            </div>
                            {menu.description && (
                              <div className={styles.menuDescription}>{menu.description}</div>
                            )}
                          </div>
                          <div className={styles.actions}>
                            <button
                              type="button"
                              className={`${styles.toggleSwitch} ${menu.isActive ? styles.toggleOn : styles.toggleOff}`}
                              onClick={() => toggleActive(menu)}
                              aria-label={menu.isActive ? '활성화됨, 클릭하여 비활성화' : '비활성화됨, 클릭하여 활성화'}
                              title={menu.isActive ? '활성 (키오스크에 노출)' : '비활성 (숨김)'}
                            >
                              <span className={styles.toggleKnob} />
                            </button>
                            <button type="button" className={styles.btnEdit} onClick={() => startEdit(menu)}>
                              수정
                            </button>
                            <button type="button" className={styles.btnDelete} onClick={() => handleDelete(menu)}>
                              삭제
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </React.Fragment>
              ))}
            </ul>
          )}

          <div className={styles.addForm}>
            <div className={styles.addRow}>
              <input
                className={styles.input}
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="메뉴 이름 (예: 비빔밥)"
                disabled={isAdding}
              />
              <input
                className={styles.priceInput}
                value={newForm.price}
                onChange={(e) => setNewForm({ ...newForm, price: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="가격"
                inputMode="numeric"
                disabled={isAdding}
              />
            </div>
            <div className={styles.addRow}>
              <input
                className={styles.input}
                value={newForm.category}
                onChange={(e) => setNewForm({ ...newForm, category: e.target.value })}
                placeholder="카테고리 (예: 커리, 난밥) — 선택"
                disabled={isAdding}
              />
            </div>
            <div className={styles.addRow}>
              <textarea
                className={styles.textarea}
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                placeholder="메뉴 설명 (선택)"
                rows={2}
                disabled={isAdding}
              />
            </div>
            <button type="button" className={styles.btnAdd} onClick={handleAdd} disabled={isAdding}>
              {isAdding ? '추가 중...' : '+ 메뉴 추가'}
            </button>
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </>
      )}
    </div>
  );
}
